import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "../components/Reveal";

const Email = () => (
    <a
        href="mailto:info@ecohouse.srl"
        data-testid="privacy-email-link"
        className="font-medium underline decoration-leaf/50 underline-offset-4 transition-colors hover:decoration-leaf"
    >
        info@ecohouse.srl
    </a>
);

const SECTIONS = [
    {
        id: "titolare-del-trattamento",
        title: "Titolare del trattamento",
        body: (
            <>
                Ecohouse S.r.l.s., P.IVA 02779610027, sede legale in Tronzano Vercellese (VC). Contatto: <Email />.
            </>
        ),
    },
    {
        id: "app-ecohouse-lavoratori",
        title: "A chi si rivolge l'app Ecohouse Lavoratori",
        body: (
            <>
                Strumento aziendale riservato a dipendenti e collaboratori di Ecohouse S.r.l.s. per registrare presenze e attività svolte in
                magazzino e nei cantieri. Non destinata al pubblico né a minori.
            </>
        ),
    },
    {
        id: "dati-trattati",
        title: "Dati trattati",
        body: (
            <>
                Dati identificativi (nome, cognome, codice fiscale, email o credenziali di accesso); presenze (data e orario di entrata e
                uscita tramite scansione QR in magazzino o cantiere); attività lavorative (fasi svolte, tempi, quantità di materiale
                installato, cantiere); firma grafica a conferma delle fasi completate; fotocamera usata solo per scansionare i QR, immagini
                non conservate salvo foto di cantiere caricate volontariamente. Il sito web non raccoglie dati personali, salvo quelli
                inviati volontariamente via email.
            </>
        ),
    },
    {
        id: "finalita-e-base-giuridica",
        title: "Finalità e base giuridica",
        body: (
            <>
                Gestione del rapporto di lavoro, rilevazione presenze, organizzazione dei cantieri e documentazione delle attività (art. 6,
                par. 1, lett. b e c GDPR) e legittimo interesse alla corretta gestione operativa (art. 6, par. 1, lett. f GDPR).
            </>
        ),
    },
    {
        id: "conservazione",
        title: "Conservazione",
        body: <>Per la durata del rapporto di lavoro e poi per i tempi previsti dalla normativa fiscale, contabile e lavoristica.</>,
    },
    {
        id: "comunicazione",
        title: "Comunicazione",
        body: (
            <>
                I dati non sono venduti né diffusi; possono essere comunicati a consulenti del lavoro, fornitori di servizi informatici e
                hosting (responsabili del trattamento) e alle autorità quando richiesto dalla legge.
            </>
        ),
    },
    {
        id: "sicurezza",
        title: "Sicurezza",
        body: <>Connessione cifrata, accesso solo al personale autorizzato.</>,
    },
    {
        id: "diritti",
        title: "Diritti",
        body: (
            <>
                Accesso, rettifica, cancellazione, limitazione, opposizione e portabilità scrivendo a <Email />; reclamo al Garante privacy (
                <a
                    href="https://www.garanteprivacy.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="privacy-garante-link"
                    className="font-medium underline decoration-leaf/50 underline-offset-4 transition-colors hover:decoration-leaf"
                >
                    www.garanteprivacy.it
                    <ArrowUpRight size={14} strokeWidth={1.75} className="mb-0.5 ml-1 inline" />
                </a>
                ).
            </>
        ),
    },
    {
        id: "cancellazione-account",
        title: "Cancellazione account",
        body: (
            <>
                Scrivere a <Email /> indicando nome e cognome; i dati che la legge obbliga a conservare saranno mantenuti solo per il tempo
                richiesto.
            </>
        ),
    },
];

export default function Privacy() {
    return (
        <article className="px-6 pb-28 pt-20 md:px-12 md:pt-32">
            <div className="max-w-[52rem]">
                <p className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-leafdeep/70 md:mb-12 md:text-xs">
                    <span className="inline-block h-px w-10 bg-leaf/60" aria-hidden="true" />
                    Ecohouse S.r.l.s.
                </p>

                <h1
                    data-testid="privacy-title"
                    className="stretch-wide text-[clamp(2.75rem,8vw,6.5rem)] font-black leading-[0.9] tracking-[-0.035em] text-leaf"
                >
                    <MaskedLine delay={0.1}>Privacy</MaskedLine>
                    <MaskedLine delay={0.22}>
                        policy<em className="font-light italic">.</em>
                    </MaskedLine>
                </h1>

                <FadeUp delay={0.4} y={20}>
                    <p data-testid="privacy-subtitle" className="mt-8 border-b border-hairline pb-10 font-mono text-xs leading-relaxed tracking-wide text-leafdeep/80 md:text-sm">
                        Informativa ai sensi del Regolamento (UE) 2016/679 (GDPR) – sito web e app Ecohouse Lavoratori.
                        <br />
                        Ultimo aggiornamento: 25 ottobre 2026.
                    </p>
                </FadeUp>

                <div>
                    {SECTIONS.map((s, i) => (
                        <FadeUp key={s.id} delay={Math.min(i * 0.04, 0.2)}>
                            <section
                                data-testid={`privacy-section-${i + 1}`}
                                className="grid grid-cols-1 gap-4 border-b border-hairline py-10 md:grid-cols-12 md:py-12"
                            >
                                <span className="font-mono text-xs text-leafdeep/50 md:col-span-2">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <div className="md:col-span-10">
                                    <h2 className="text-xl font-extrabold tracking-tight text-leaf md:text-2xl">{s.title}</h2>
                                    <p className="mt-4 text-base leading-relaxed text-leafdeep/90">{s.body}</p>
                                </div>
                            </section>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </article>
    );
}
